import { useRequest } from 'ahooks';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, Loading, MessagePlugin } from 'tdesign-react';

import { Star3 } from '@/components/icon';
import { AIGC_VIDEO_PROMPT_LENGTH, AiType, AiVideoReq, AiVideoType, GetAIResourceRsp, ResourceHistory, Status, UrlInfo, UserLevel, VideoImage, VideoImageRole } from '@/interface/aigc';
import { aiVideo, getAIResource } from '@/services/aigc';
import { CgiErrorCode, CgiResp } from '@/services/cgi';
import { getUnicodeLength } from '@/utils/getUnicodeLength';

import { getUsageExceedMessage } from '../AigcImageComponent/util';
import { AigcVideoDialogProps } from '../AigcVideoDialog';
import AigcVideoHistory from './history';
import styles from './index.module.less';
import AigcVideoInfo, { AigcVideoInfoRef } from './info';

interface AigcVideoProps {
  visible: boolean;
  data: AigcVideoDialogProps['data'];
  isHorizontal: boolean;
  getSrc: (src: UrlInfo) => void;
  getPrompt?: (prompt: string) => void;
  pagination: {
    page: number;
    size: number;
    total: number;
  };
  onPageChange: (pageInfo: {
    page: number,
    size: number,
    total: number,
  }) => void;
}

const FAILED_MESSAGE = 'AI视频生成失败，请点击重试';
const TIMEOUT = 60000 * 6;
const AIGC_VIDEO_COUNT = 1;

const AigcVideoComponent: React.FC<AigcVideoProps> = (props) => {
  const { visible, data, isHorizontal, pagination, onPageChange, getSrc, getPrompt } = props;
  const [histories, setHistories] = useState<ResourceHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<UserLevel>();

  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const aigcVideoInfoRef = useRef<AigcVideoInfoRef>(null);

  useEffect(() => {
    if (visible) {
      setLoading(true);
      getVideo();
    } else {
      cleanup({ cleanHistories: false });
    }

    return () => cleanup({ cleanHistories: false });
  }, [visible, pagination.page, pagination.size]);

  const generate = async () => {
    aigcVideoInfoRef.current?.submitForm();
    const videoInfo = aigcVideoInfoRef.current?.getVideoInfo();
    const { type, text, duration, imageUrl = [] } = videoInfo || {};
    const prompt = text?.prompt || '';
    const firstImage = imageUrl?.find((e: VideoImage) => e.role === VideoImageRole.FIRST)?.url || '';

    let errorMessage = null;
    if (prompt?.trim().length === 0) {
      errorMessage = '请填写画面描述';
    } else if (getUnicodeLength(prompt) > AIGC_VIDEO_PROMPT_LENGTH) {
      errorMessage = `最多输入${AIGC_VIDEO_PROMPT_LENGTH}字`;
    } else if (type === AiVideoType.IMAGE && !firstImage) {
      errorMessage = '请上传首帧图片';
    }
    if (errorMessage) {
      MessagePlugin.error({ content: errorMessage });
      return;
    }

    const postData: AiVideoReq = {
      queryId: '',
      type,
      content: {
        text: prompt,
        duration,
        ratio: isHorizontal ? '16:9' : '9:16',
        imageUrl,
      },
    };

    await generateVideo(postData);
  };

  const generateVideo = async (postData: AiVideoReq) => {
    onPageChange({ ...pagination, page: 1 });

    const { code, data, message } = await aiVideo(postData);
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || FAILED_MESSAGE,
      });
      if (code === CgiErrorCode.BlackUser) {
        setUser(UserLevel.BLACK);
      } else if (code === CgiErrorCode.UsageExceed) {
        setUser(data?.userLevelType);
      }
      cleanup({ cancelPoll: false, cleanHistories: false });
      return;
    }

    setHistories(pre => [
      {
        urls: Array(AIGC_VIDEO_COUNT).fill({ url: 'initUrl', name: '' }),
        prompt: postData.content?.text || '',
        status: Status.WAITING,
        relateId: data?.relateId,
        ratio: isHorizontal ? '16:9' : '9:16',
      },
      ...pre,
    ]);

    // 开始轮询
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    delayTimerRef.current = setTimeout(() => {
      getVideo();
    }, 1000);
  };

  const getVideo = () => {
    startPolling();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setTimeout(() => {
      MessagePlugin.error('生成超时，请点击重试');
      cleanup();
    }, TIMEOUT);
  };

  const { data: videoRes, run: startPolling, cancel: cancelPolling } = useRequest(
    () => getAIResource({
      queryId: '',
      type: AiType.VIDEO,
      page: pagination.page,
      size: pagination.size,
    }),
    {
      pollingInterval: 5000,
      manual: true,
      pollingWhenHidden: false,
    },
  );

  useEffect(() => {
    if (videoRes) setLoading(false);
    const { code, data } = videoRes as CgiResp<GetAIResourceRsp> || { code: 0, data: {} };
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error(FAILED_MESSAGE);
      cleanup();
      return;
    }
    if (isEmpty(data) || isEmpty(data.data)) {
      return;
    }

    onPageChange({ ...pagination, total: data.totalCnt || 0 });
    setHistories(data.data);
    const isFinished = data.data.every(e => e.status === Status.FINISHED);
    if (isFinished) {
      cleanup();
    }
  }, [videoRes, cancelPolling]);

  const cleanup = (params?: {
    cancelPoll?: boolean,
    cleanHistories?: boolean,
  }) => {
    const { cancelPoll = true, cleanHistories = true } = params || {};
    if (delayTimerRef.current && cancelPoll) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (cancelPoll) {
      cancelPolling();
    }

    if (cleanHistories) {
      setHistories(pre => pre.map(history => ({
        ...history,
        status: Status.FINISHED,
      })));
    };
  };

  const handleDelete = (id: string) => {
    const newHistories = histories.filter(e => e.relateId !== id);
    setHistories(newHistories);
  };

  const errMsg = useMemo(() => getUsageExceedMessage(AiType.VIDEO, user), [user]);

  return (
    <div className={styles.aigcVideoComponent}>
      <div className={styles.left}>
        <div className={styles.controlPanel}>
          <AigcVideoInfo
            ref={aigcVideoInfoRef}
            data={data}
            getPrompt={getPrompt}
          />
        </div>
        <div className={styles.generateButtonContainer}>
          <Button
            onClick={generate}
            className={styles.geneBtn}
            theme='default'
          >
            <div className={styles.btnText}>
              <Star3 />生成视频
            </div>
          </Button>
        </div>
      </div>
      <div className={styles.videoDisplay}>
        <Loading
          loading={loading}
          text='加载中...'
          style={{ display: 'flex', flex: 1 }}
        >
          <AigcVideoHistory
            histories={histories}
            generateVideo={generateVideo}
            getSrc={getSrc}
            onDelete={handleDelete}
            isHorizontal={isHorizontal}
            pagination={pagination}
            loading={loading}
          />
        </Loading>
      </div>

      {errMsg && <Dialog
        theme='danger'
        visible={user !== undefined}
        width={400}
        placement='center'
        cancelBtn={false}
        closeBtn={false}
        onConfirm={() => setUser(undefined)}
        header={errMsg.title}
      >
        <div style={{ paddingTop: 10 }}>{errMsg.desc}</div>
      </Dialog>}
    </div>
  );
};

export default AigcVideoComponent;
